import { z } from "zod";
import { PLATFORM_TYPES } from "./constants.js";
import { apiIntIdSchema } from "./ids.js";
import { contactImportResultSchema } from "./contact-contracts.js";
import { CONTACT_IMPORT_BIRTHDAY_FORMATS, CONTACT_IMPORT_BIRTHDAY_ORDERS } from "./contact-import-birthday.js";
import { contactImportColumnMappingSchema } from "./contact-import-mapping.js";
export const platformTypeNameSchema = z.enum(PLATFORM_TYPES);
export const platformConnectionStatusSchema = z.enum([
    "PENDING",
    "ACTIVE",
    "DISCONNECTED",
]);
export const contactPhoneMatchSchema = z.object({
    contactId: apiIntIdSchema,
    displayName: z.string(),
    normalizedPhoneNumber: z.string().nullable(),
    email: z.string().nullable(),
    linkedPlatformTypes: z.array(platformTypeNameSchema),
});
export const contactPhoneMatchResponseSchema = z.object({
    matches: z.array(contactPhoneMatchSchema),
});
export const contactImportPreviewRowStatusSchema = z.enum([
    "ready",
    "invalid",
    "duplicate_contact",
    "exact_unlinked_profile_match",
    "ambiguous_profile_match",
    "restore_deleted_contact",
    "ambiguous_deleted_phone",
]);
/** Preview-only. Execution does not read this. */
export const contactImportDuplicateOriginSchema = z.enum(["existing_contact", "in_file"]);
export const contactImportPreviewRowSchema = z.object({
    row: z.number().int().positive(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    displayName: z.string().nullable(),
    email: z.string().nullable(),
    originalPhone: z.string().nullable(),
    normalizedPhone: z.string().nullable(),
    /** Canonical `YYYY-MM-DD` when the mapped birthday cell is valid. */
    birthday: z.string().nullable().optional(),
    /** Non-blocking row warnings. A birthday warning does not change `status`. */
    warningCodes: z.array(z.string()).max(8).optional(),
    groups: z.array(z.string()),
    status: contactImportPreviewRowStatusSchema,
    message: z.string().nullable(),
    messageCode: z.string().nullable().optional(),
    matchedProfileId: apiIntIdSchema.nullable().optional(),
    /** Set only for duplicate_contact. Absent on older preview payloads. */
    duplicateOrigin: contactImportDuplicateOriginSchema.nullable().optional(),
});
export const contactImportPreviewRequestSchema = z.object({
    csv: z.string().min(1),
    defaultCountryIso: z.string().trim().length(2).optional(),
    autoLinkExactPhoneMatches: z.boolean().optional(),
    columnMapping: contactImportColumnMappingSchema.optional(),
    birthdayFormat: z.enum(CONTACT_IMPORT_BIRTHDAY_FORMATS).optional(),
    applyGroupIds: z.array(apiIntIdSchema).max(50).optional(),
});
/**
 * Aggregate counts from the existing row classifier.
 * Read-only. Not a raw file row count and not a capacity reservation.
 */
export const contactImportClassificationCountsSchema = z.object({
    ready: z.number().int().nonnegative(),
    restored: z.number().int().nonnegative(),
    duplicate: z.number().int().nonnegative(),
    invalid: z.number().int().nonnegative(),
    ambiguousDeleted: z.number().int().nonnegative(),
    exactUnlinked: z.number().int().nonnegative(),
    ambiguousProfile: z.number().int().nonnegative(),
    /**
     * Split of `duplicate` for preview copy. Defaulted so a newer Web can parse
     * an older API payload that only sent `duplicate`.
     */
    duplicateExisting: z.number().int().nonnegative().default(0),
    duplicateInFile: z.number().int().nonnegative().default(0),
});
/** Informational quota snapshot. Does not reserve capacity. */
export const contactImportCapacityPreviewSchema = z.object({
    /** Non-deleted contacts currently occupying the organization quota. */
    contactsUsed: z.number().int().nonnegative(),
    /** Null means Unlimited. */
    contactsLimit: z.number().int().nonnegative().nullable(),
    /** Null when the limit is Unlimited. Otherwise max(0, limit - used). */
    remainingCapacity: z.number().int().nonnegative().nullable(),
    /** Rows that would create or restore a non-deleted contact. Not the raw file row count. */
    contactsRequiredByImport: z.number().int().nonnegative(),
    /** False when a finite limit cannot hold every quota-consuming row. */
    fitsContactLimit: z.boolean(),
    /** Existing classifier totals. No per-row payload. */
    classification: contactImportClassificationCountsSchema,
    /**
     * Birthday-column inference for the mapped file.
     * `ambiguous` means Auto cannot choose an order. Absent on older payloads.
     */
    birthdayImport: z
        .object({
        ambiguous: z.boolean(),
        inferredFormat: z.enum(CONTACT_IMPORT_BIRTHDAY_ORDERS).nullable(),
    })
        .optional(),
});
export const contactImportPreviewResponseSchema = z
    .object({
    rows: z.array(contactImportPreviewRowSchema),
    readyCount: z.number().int().nonnegative(),
    invalidCount: z.number().int().nonnegative(),
    duplicateCount: z.number().int().nonnegative(),
    restoreCount: z.number().int().nonnegative().optional(),
})
    .merge(contactImportCapacityPreviewSchema);
export const contactImportExecuteRequestSchema = z.object({
    csv: z.string().min(1),
    defaultCountryIso: z.string().trim().length(2).optional(),
    autoLinkExactPhoneMatches: z.boolean().optional(),
    columnMapping: contactImportColumnMappingSchema.optional(),
    birthdayFormat: z.enum(CONTACT_IMPORT_BIRTHDAY_FORMATS).optional(),
    applyGroupIds: z.array(apiIntIdSchema).max(50).optional(),
});
export const contactMergePreviewRequestSchema = z.object({
    primaryContactId: apiIntIdSchema,
    secondaryContactId: apiIntIdSchema,
    survivingPhoneContactId: apiIntIdSchema.optional(),
    survivingEmailContactId: apiIntIdSchema.optional(),
    survivingBirthdayContactId: apiIntIdSchema.optional(),
});
export const contactMergePreviewResponseSchema = z.object({
    canMerge: z.boolean(),
    blockingReason: z.string().nullable(),
    blockingCode: z.enum(["SELF_MERGE", "PLATFORM_CONFLICT"]).nullable().optional(),
    blockingPlatformType: platformTypeNameSchema.nullable().optional(),
    primaryContactId: apiIntIdSchema,
    secondaryContactId: apiIntIdSchema,
    resultingDisplayName: z.string(),
    resultingPhone: z.string().nullable(),
    resultingEmail: z.string().nullable(),
    resultingPlatformTypes: z.array(platformTypeNameSchema),
    resultingGroups: z.array(z.string()),
    phoneConflict: z.boolean(),
    emailConflict: z.boolean(),
    whatsappPhoneAuthoritative: z.boolean(),
    primaryBirthday: z.string().nullable(),
    secondaryBirthday: z.string().nullable(),
    /** Both contacts have different birthdays; the merge needs an explicit choice. */
    birthdayConflict: z.boolean(),
    /** Null while a birthday conflict is unresolved. */
    resultingBirthday: z.string().nullable(),
});
export const contactMergeExecuteRequestSchema = z.object({
    primaryContactId: apiIntIdSchema,
    secondaryContactId: apiIntIdSchema,
    survivingPhoneContactId: apiIntIdSchema.optional(),
    survivingEmailContactId: apiIntIdSchema.optional(),
    /** Required when both contacts have different birthdays. */
    survivingBirthdayContactId: apiIntIdSchema.optional(),
});
export const contactMergeExecuteResponseSchema = z.object({
    contactId: apiIntIdSchema,
});
export { contactImportResultSchema };
//# sourceMappingURL=contact-identity-contracts.js.map